import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Heart, Eye, EyeOff, CheckCircle, XCircle, Lock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

// Use relative URL for same-origin API calls (works in production)
const API = '/api';

const ResetPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setTokenValid(false);
        setVerifying(false);
        return;
      }

      try {
        await axios.get(`${API}/auth/verify-reset-token?token=${token}`);
        setTokenValid(true);
      } catch (error) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: password
      });
      setResetSuccess(true);
      toast.success('Password reset successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#881337] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#57534E]">Verifying reset link...</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="text-[#881337]" size={32} fill="#881337" />
              <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <XCircle className="w-8 h-8 text-red-600" />
            </div>
            
            <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-4">Invalid or Expired Link</h2>
            
            <p className="text-[#57534E] mb-6">
              This password reset link is invalid or has expired. Reset links are only valid for 15 minutes.
            </p>

            <div className="space-y-4">
              <Button
                onClick={() => navigate('/forgot-password')}
                className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white"
              >
                Request New Reset Link
              </Button>
              
              <Link
                to="/auth"
                className="block text-center text-[#881337] font-medium hover:underline"
              >
                Back to Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Heart className="text-[#881337]" size={32} fill="#881337" />
              <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
            </div>
          </div>

          <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            
            <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-4">Password Reset!</h2>
            
            <p className="text-[#57534E] mb-6">
              Your password has been successfully reset. You can now log in with your new password.
            </p>

            <Button
              onClick={() => navigate('/auth')}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white"
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAFAF9] to-[#F5F5F4] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="text-[#881337]" size={32} fill="#881337" />
            <span className="text-3xl font-serif font-bold tracking-tight text-[#1C1917]">VowFlow</span>
          </div>
          <p className="text-[#57534E]">Create your new password</p>
        </div>

        <div className="bg-white/70 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/50 p-8">
          <div className="w-14 h-14 bg-[#881337]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Lock className="w-7 h-7 text-[#881337]" />
          </div>

          <h2 className="text-2xl font-serif font-bold text-[#1C1917] mb-2 text-center">Reset Password</h2>
          <p className="text-[#57534E] text-center mb-6 text-sm">
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="password" className="text-[#1C1917] mb-2 block">New Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0 pr-10"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917] transition-colors"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-[#78716C] mt-1">Must be at least 8 characters</p>
            </div>
            
            <div>
              <Label htmlFor="confirmPassword" className="text-[#1C1917] mb-2 block">Confirm Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="border-b border-stone-300 focus:border-stone-800 bg-transparent rounded-none px-0 pr-10"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-0 top-1/2 -translate-y-1/2 text-[#78716C] hover:text-[#1C1917] transition-colors"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            <Button 
              type="submit" 
              disabled={loading}
              className="w-full rounded-full py-6 bg-[#881337] hover:bg-[#9F1239] text-white transition-all duration-300"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
